/**
 * File upload validation constants
 * These should match the backend validation rules
 */

export const AVATAR_UPLOAD = {
  MAX_SIZE_BYTES: 5 * 1024 * 1024, // 5MB
  MAX_SIZE_MB: 5,
  ALLOWED_MIME_TYPES: ["image/jpeg", "image/png", "image/gif", "image/webp"] as const,
  ALLOWED_EXTENSIONS: [".jpg", ".jpeg", ".png", ".gif", ".webp"] as const,
  ACCEPT_STRING: "image/jpeg,image/png,image/gif,image/webp"
} as const;

export const DOCUMENT_AI_UPLOAD = {
  MAX_FILE_SIZE_BYTES: 20 * 1024 * 1024, // 20MB
  MAX_FILE_SIZE_MB: 20,
  MAX_FILES: 10,
  MAX_TOTAL_SIZE_BYTES: 100 * 1024 * 1024, // 100MB
  MAX_TOTAL_SIZE_MB: 100
} as const;

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Validate file for avatar upload
 */
export function validateAvatarFile(file: File): string | null {
  // Check file type by MIME type
  if (!AVATAR_UPLOAD.ALLOWED_MIME_TYPES.includes(file.type as any)) {
    return "Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.";
  }

  // Additional check: validate file extension
  const fileName = file.name.toLowerCase();
  const hasValidExtension = AVATAR_UPLOAD.ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
  if (!hasValidExtension) {
    return "Invalid file extension. Please upload a JPEG, PNG, GIF, or WebP image.";
  }

  // Check file size
  if (file.size > AVATAR_UPLOAD.MAX_SIZE_BYTES) {
    const fileSize = formatFileSize(file.size);
    return `File is too large (${fileSize}). Maximum size is ${AVATAR_UPLOAD.MAX_SIZE_MB}MB.`;
  }

  // Check if file is empty
  if (file.size === 0) {
    return "File is empty. Please select a valid image.";
  }

  return null; // File is valid
}

/**
 * Validate files for AI processing
 */
export function validateAiFiles(files: File[]): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  let totalSize = 0;

  if (files.length > DOCUMENT_AI_UPLOAD.MAX_FILES) {
    errors.push(`You can upload a maximum of ${DOCUMENT_AI_UPLOAD.MAX_FILES} files.`);
  }

  for (const file of files) {
    totalSize += file.size;

    if (file.size > DOCUMENT_AI_UPLOAD.MAX_FILE_SIZE_BYTES) {
      errors.push(
        `${file.name} (${formatFileSize(file.size)}) exceeds the maximum file size of ${DOCUMENT_AI_UPLOAD.MAX_FILE_SIZE_MB}MB.`
      );
    }

    if (file.size === 0) {
      errors.push(`${file.name} is empty.`);
    }
  }

  if (totalSize > DOCUMENT_AI_UPLOAD.MAX_TOTAL_SIZE_BYTES) {
    errors.push(
      `Total upload size (${formatFileSize(totalSize)}) exceeds the maximum of ${DOCUMENT_AI_UPLOAD.MAX_TOTAL_SIZE_MB}MB.`
    );
  }

  return { valid: errors.length === 0, errors };
}
